// Menu screens: title, star map, crew assembly, codex, how-to-play, settings, pause, results.
import { LEVELS, RECIPES, INGREDIENTS, PERKS, CHEF_COLORS, CHEF_NAMES, starGoals, clockText } from '../game/data.js';
import { DEVICES, PAD_STYLES, padHints, input, kbHints, keyCap, MOUSE_GLYPH } from '../engine/input.js';
import { audio } from '../engine/audio.js';
import { isBot, hasRoomForPerson, crewKind, DELEGATE_TASKS, BOT_WAYS } from '../game/helper.js';

/** A bot helper's crew card: it does the chores, and whatever you Delegate to it. */
const botSlot = (i) => `<div class="avatar bot" style="--c:${CHEF_COLORS[i]}"><div class="helm"></div><div class="face"><i></i><i></i></div><span class="bot-tag">🤖</span></div>
  <div class="sl-name" style="color:${CHEF_COLORS[i]}">${CHEF_NAMES[i]}</div>
  <div class="sl-dev">Bot helper<small>Does the chores, plus what you Delegate</small></div>`;
/** Key hints on a crew card (the swap key is Delegate, shown only when there's a bot to delegate to). */
const keyList = (hi, bots) => Object.entries(hi).filter(([k]) => bots || k !== 'swap').map(([k, v]) => `<span><b>${v}</b> ${k === 'swap' ? 'delegate' : k}</span>`).join('');
/** The kinds of crew best scores are kept apart for, as a shift card or a results card names them. */
const CREW_KINDS = { humans: 'an all-human crew', mixed: 'a mixed crew', bots: 'an all-bot crew' };
/** How someone asking gets a bot helper: they add it themselves (offline, or as the host), or ask the host for it. */
const getBot = (addsBots, which = 'a') => `${addsBots ? 'add' : 'ask the host for'} ${which} bot helper`;
/**
 * Why a crew's last chef can't step out or be removed: a shift always needs someone at the stoves, bot or person, so
 * another chef has to arrive first. The note names every way that can happen for whoever is asking:
 * 	- the last chef is a bot, so the people here are all watching: one of them joins the crew, or another bot helper comes;
 * 	- the last chef is a person: someone else joins, or a bot helper comes.
 */
const lastChefNote = (chef, addsBots) => isBot(chef)
  ? `At least one chef has to cook: join the crew or ${getBot(addsBots, 'another')} first.`
  : `At least one chef has to cook: have someone join or ${getBot(addsBots)} first.`;
/** A crew with no chef at all (online, when its last cook left): the same two ways back. */
export const emptyCrewNote = (addsBots) => `The crew needs at least one chef: join it or ${getBot(addsBots)}.`;
/** Why nobody else can take a chef: four people already cook, with no bot to give up its place. */
const CREW_FULL = 'Four people are already cooking: someone has to step out first.';
/** Why the last person on this screen can't leave it: someone stays, to watch or to cook. */
const LAST_PERSON = 'Someone has to stay here, to watch or to cook.';

const h = (tag, cls, html) => {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (html !== undefined) e.innerHTML = html;
  return e;
};
const starStr = (n, max = 3) => Array.from({ length: max }, (_, i) => `<i class="${i < n ? 'on' : ''}">★</i>`).join('');

export class Screens {
  constructor(app) {
    this.app = app;
    this.root = document.getElementById('ui');
    this.current = null;
    this.stack = [];
    input.onPress((code) => this._nav(code));
    document.addEventListener('mouseover', (e) => {
      const b = e.target.closest && e.target.closest('.nav');
      if (b && b !== this._lastHover) {
        this._lastHover = b;
        audio.sfx('uiHover');
      }
    });
  }
  img(key, cls = 'ic') {
    return `<img class="${cls}" src="${this.app.icons[key] || ''}" alt="">`;
  }
  ingIcon(id, prepared = true) {
    const d = INGREDIENTS[id];
    const st = !prepared ? 'raw' : d.prep === 'chop' ? 'chopped' : d.prep === 'grind' ? 'ground' : 'raw';
    return this.img('ing:' + id + ':' + st);
  }

  show(name, opts = {}) {
    const fn = this['_' + name];
    if (!fn) return;
    if (this.current && !opts.noStack && !opts.replace) this.stack.push(this.current.name);
    if (opts.replace) this.stack = [];
    this.root.innerHTML = '';
    const el = fn.call(this, opts);
    el.classList.add('screen', 'screen-' + name);
    this.root.appendChild(el);
    this.current = { name, el, opts };
    this.app.menu && (this.app.menu.mode = { title: 'title', map: 'map', crew: 'crew', online: 'crew', codex: 'book', settings: 'settings', howto: 'book', credits: 'title' }[name] || this.app.menu.mode);
    requestAnimationFrame(() => el.classList.add('in'));
    this._focusFirst();
  }
  back() {
    const prev = this.stack.pop();
    audio.sfx('uiBack');
    if (prev) this.show(prev, { noStack: true });
  }
  tick() {
    if (this.current && this.current.name === 'crew' && this._crewPoll) this._crewPoll();
    if (this.current && this.current.name === 'online' && this._onlinePoll) this._onlinePoll();
  }
  /** A short note that floats over any screen for a few seconds. */
  toast(text) {
    const t = h('div', 'ui-toast', text);
    document.body.appendChild(t);
    setTimeout(() => t.classList.add('out'), 4200);
    setTimeout(() => t.remove(), 4800);
  }
  clear() {
    this.root.innerHTML = '';
    this.current = null;
    this.stack = [];
  }
  _btn(label, onClick, cls = '') {
    const b = h('button', 'btn nav ' + cls, label);
    b.addEventListener('click', (e) => {
      e.stopPropagation();
      audio.init();
      audio.sfx('uiClick');
      onClick();
    });
    return b;
  }
  /** The spectators under the crew cards: each one's name, with a Join button for the ones on this screen. */
  _spectatorStrip(el, list) {
    el.innerHTML = '';
    el.classList.toggle('on', list.length > 0);
    if (!list.length) return;
    el.appendChild(h('span', 'cw-l', 'Spectating'));
    for (const w of list) {
      const pill = h('span', 'cw-p', w.label);
      if (w.cook) pill.appendChild(this._btn('Join', w.cook));
      el.appendChild(pill);
    }
  }
  _focusFirst() {
    const el = this.current && this.current.el.querySelector('.nav:not([disabled])');
    if (el && this._keyboardMode) el.focus();
  }
  _nav(code) {
    if (!this.current || this.app.state === 'game' && !this.app.paused) return;
    if (this.current.name === 'crew') return; // crew screen captures raw input
    const pad = code.startsWith('Pad');
    const b = pad ? +code.split(':')[1] : -1;
    const up = code === 'ArrowUp' || code === 'KeyW' || b === 12;
    const down = code === 'ArrowDown' || code === 'KeyS' || b === 13;
    const left = code === 'ArrowLeft' || code === 'KeyA' || b === 14;
    const right = code === 'ArrowRight' || code === 'KeyD' || b === 15;
    const mb = pad ? input.menuButtons(+code.slice(3).split(':')[0]) : null;
    const ok = code === 'Enter' || code === 'Space' || (pad && b === mb.ok);
    const back = code === 'Escape' || code === 'Backspace' || (pad && b === mb.back);
    if (this.current.name === 'splash') {
      if (ok || pad || code) this.current.opts.onGo && this.current.opts.onGo();
      return;
    }
    const all = [...this.current.el.querySelectorAll('.nav:not([disabled])')].filter((e) => e.offsetParent);
    if (!all.length) return;
    let cur = document.activeElement && all.includes(document.activeElement) ? document.activeElement : null;
    if (up || down || left || right) {
      this._keyboardMode = true;
      if (!cur) {
        all[0].focus();
        return;
      }
      const r0 = cur.getBoundingClientRect();
      const c0 = [r0.left + r0.width / 2, r0.top + r0.height / 2];
      let best = null, bs = 1e9;
      for (const e of all) {
        if (e === cur) continue;
        const r = e.getBoundingClientRect();
        const c = [r.left + r.width / 2, r.top + r.height / 2];
        const dx = c[0] - c0[0], dy = c[1] - c0[1];
        let ok2 = false, score = 0;
        if (up) { ok2 = dy < -4; score = -dy + Math.abs(dx) * 2.5; }
        if (down) { ok2 = dy > 4; score = dy + Math.abs(dx) * 2.5; }
        if (left) { ok2 = dx < -4; score = -dx + Math.abs(dy) * 2.5; }
        if (right) { ok2 = dx > 4; score = dx + Math.abs(dy) * 2.5; }
        if (ok2 && score < bs) { bs = score; best = e; }
      }
      if (best) {
        best.focus();
        best.dispatchEvent(new Event('navfocus'));
        audio.sfx('uiHover');
      }
    } else if (ok && pad && cur) {
      cur.click();
    } else if (back) {
      if (this.current.opts.onBack) this.current.opts.onBack();
      else if (this.stack.length) this.back();
    }
  }

  // ------------------------------------------------------------ splash
  _splash(opts) {
    const el = h('div', '', `
      <div class="logo big">
        <div class="logo-kicker">An Interstellar Teahouse</div>
        <h1 class="logo-title"><span>Flying</span> <span>Saucer</span></h1>
      </div>
      <div class="press">Press ${keyCap('any key')} or ${MOUSE_GLYPH} click to begin</div>`);
    el.addEventListener('click', () => opts.onGo());
    return el;
  }

  // ------------------------------------------------------------ title
  _title() {
    const app = this.app;
    const el = h('div');
    const logo = h('div', 'logo', `
      <div class="logo-kicker">An Interstellar Teahouse</div>
      <h1 class="logo-title"><span>Flying</span> <span>Saucer</span></h1>`);
    el.appendChild(logo);
    const menu = h('div', 'menu');
    const total = app.totalStars();
    menu.appendChild(this._btn(`<span class="bi">✦</span> ${total ? 'Continue Shift' : 'Start Shift'}`, () => this.show('map'), 'primary'));
    const nb = app.players.filter(isBot).length;
    const crewLabel = app.onlyBotsCook ? `Spectating · ${nb} bot${nb > 1 ? 's' : ''}` : app.players.length === 1 ? 'Solo' : app.players.length + ' players';
    menu.appendChild(this._btn(`<span class="bi">☺</span> Crew <em>${crewLabel}</em>`, () => this.show('crew')));
    menu.appendChild(this._btn('<span class="bi">✧</span> Play Online <em>with friends anywhere</em>', () => this.show('online')));
    menu.appendChild(this._btn('<span class="bi">❦</span> Recipe Codex', () => this.show('codex')));
    menu.appendChild(this._btn('<span class="bi">?</span> How to Play', () => this.show('howto')));
    menu.appendChild(this._btn('<span class="bi">⚙</span> Settings', () => this.show('settings')));
    menu.appendChild(this._btn('<span class="bi">♥</span> Credits', () => this.show('credits')));
    el.appendChild(menu);
    const foot = h('div', 'title-foot', `<span>${total} / ${LEVELS.length * 3} ★ earned</span><span>Tip: plug in gamepads for couch co-op</span>`);
    el.appendChild(foot);
    return el;
  }

  // ------------------------------------------------------------ star map
  _map() {
    const app = this.app;
    const el = h('div');
    el.appendChild(h('div', 'screen-head', `<h2>Star Route</h2><div class="sh-sub">Pick tonight's shift · <b>${app.totalStars()}</b> ★ collected</div>`));
    const map = h('div', 'starmap');
    const svg = [`<svg class="route" viewBox="0 0 1000 600" preserveAspectRatio="none">`];
    for (let i = 0; i < LEVELS.length - 1; i++) {
      const a = LEVELS[i].pos, b = LEVELS[i + 1].pos;
      const un = app.isUnlocked(i + 1);
      svg.push(`<path class="${un ? 'on' : ''}" d="M${a[0] * 1000},${a[1] * 600} Q${((a[0] + b[0]) / 2) * 1000},${((a[1] + b[1]) / 2) * 600 - 60} ${b[0] * 1000},${b[1] * 600}"/>`);
    }
    svg.push('</svg>');
    map.innerHTML = svg.join('');
    const card = h('div', 'level-card');
    LEVELS.forEach((L, i) => {
      const un = app.isUnlocked(i);
      const st = app.save.stars[L.id] || 0;
      const node = h('button', 'planet nav' + (un ? '' : ' locked'), `
        <span class="orb" style="--a:${L.theme.planet};--b:${L.theme.planet2};--g:${L.theme.accent}"><span class="ring"></span></span>
        <span class="pn">${i + 1}. ${L.name}</span>
        <span class="ps">${un ? starStr(st) : '🔒'}</span>`);
      node.style.left = L.pos[0] * 100 + '%';
      node.style.top = L.pos[1] * 100 + '%';
      const sel = () => this._showLevelCard(card, i);
      node.addEventListener('mouseenter', sel);
      node.addEventListener('focus', sel);
      node.addEventListener('click', () => {
        audio.sfx(un ? 'uiClick' : 'wrong');
        if (un) app.startLevel(i);
      });
      map.appendChild(node);
    });
    el.appendChild(map);
    el.appendChild(card);
    const last = Math.max(0, LEVELS.findIndex((L, i) => app.isUnlocked(i) && !app.save.stars[L.id]));
    this._showLevelCard(card, last === -1 ? 0 : last);
    el.appendChild(this._btn('← Back', () => this.back(), 'back'));
    return el;
  }
  _showLevelCard(card, i) {
    const app = this.app, L = LEVELS[i];
    const un = app.isUnlocked(i);
    // the bests for the kind of crew set up right now (all humans, mixed, or all bots)
    const kind = crewKind(app.online ? app.online.crew : app.players);
    const best = app.bestFor(L.id, kind), bestInf = app.bestFor(L.id, kind, true);
    const st = app.save.stars[L.id] || 0;
    const crew = app.online ? app.online.crew.length : app.players.length;
    card.style.setProperty('--acc', L.theme.accent);
    card.innerHTML = `
      <div class="lc-num">Shift ${i + 1} · ${L.sub}</div>
      <div class="lc-name">${L.name}</div>
      <div class="lc-twist">${un ? L.twistText : 'Earn at least one ★ on the previous shift to unlock.'}</div>
      <div class="lc-menu">${L.recipes.map((r) => `<div class="lc-r" title="${RECIPES[r].name}">${this.img('drink:' + r)}<span>${un ? RECIPES[r].name : '???'}</span></div>`).join('')}</div>
      <div class="lc-goals">${starGoals(L, crew).map((s, k) => `<span class="${st > k ? 'got' : ''}">★ ${s}</span>`).join('')}${crew > 1 ? `<small>for ${crew} players</small>` : ''}</div>
      <div class="lc-best">Best with ${CREW_KINDS[kind]}: <b>${best}</b> coins · ${clockText(L.time)} shift${bestInf ? ` · infinite: <b>${bestInf}</b>` : ''}</div>`;
    if (un) {
      // a timed shift, or an infinite one that runs until someone ends it from the pause menu
      const row = h('div', 'lc-start');
      row.appendChild(this._btn('Start Shift →', () => app.startLevel(i), 'primary'));
      row.appendChild(this._btn('Infinite ∞', () => app.startLevel(i, { infinite: true }), 'infinite'));
      card.appendChild(row);
    }
  }

  // ------------------------------------------------------------ crew
  _crew() {
    const app = this.app;
    const el = h('div');
    el.appendChild(h('div', 'screen-head', `<h2>Assemble the Crew</h2><div class="sh-sub crew-sub"></div>`));
    const sub = el.querySelector('.crew-sub');
    const slots = h('div', 'crew-slots');
    el.appendChild(slots);
    const watching = h('div', 'crew-watch');
    el.appendChild(watching);
    const devs = () => {
      const list = ['kbA', 'kbB'];
      for (const p of input.pads()) list.push('pad' + p.index);
      return list;
    };
    const devLabel = (d) => (d.startsWith('kb') ? DEVICES[d].label : 'Gamepad ' + (+d.slice(3) + 1));
    const hint = (d) => input.hintsFor(d);
    const render = () => {
      const P = padHints(input.anyPadStyle);
      const nPads = input.pads().length;
      sub.innerHTML = `Up to 4 baristas on one screen. To join press ${keyCap('Space')} (keyboard 1), ${keyCap('Enter')} (keyboard 2) or ${P.grab} on a controller. To step out and spectate press dash (${keyCap('LShift')} / ${keyCap('RCtrl')} / ${P.dash}), and press it again to leave.<small>${nPads ? nPads + ' controller' + (nPads > 1 ? 's' : '') + ' connected' : 'No controller detected yet: connect one and press any button'}</small>`;
      slots.innerHTML = '';
      const bots = app.players.some(isBot);
      for (let i = 0; i < 4; i++) {
        const p = app.players[i];
        if (p && isBot(p)) {
          const s = h('div', 'slot filled bot', botSlot(i));
          s.appendChild(this._btn('Remove', () => {
            if (app.players.length <= 1) return this.toast(lastChefNote(p, true));
            app.players.splice(app.players.indexOf(p), 1);
            app.saveBots(app.players.filter(isBot).length);
            audio.sfx('uiBack');
            render();
          }));
          slots.appendChild(s);
          continue;
        }
        const s = h('div', 'slot' + (p ? ' filled' : ''), p
          ? `<div class="avatar" style="--c:${CHEF_COLORS[i]}"><div class="helm"></div><div class="face"><i></i><i></i></div></div>
             <div class="sl-name" style="color:${CHEF_COLORS[i]}">${CHEF_NAMES[i]}</div>
             <div class="sl-dev">${devLabel(p.device)}${p.device.startsWith('pad') ? `<small>${PAD_STYLES[input.padStyle(+p.device.slice(3))].label} buttons${input.isStandard(+p.device.slice(3)) ? '' : ' · <b class="warn">non-standard mode: switch the controller to PC / X-input mode</b>'}</small>` : ''}</div>
             <div class="sl-keys">${keyList(hint(p.device), bots)}</div>
             <div class="sl-ready">Press ${hint(p.device).grab.split('<span class="kor">')[0]} again to start</div>`
          : `<div class="avatar empty"></div><div class="sl-name">Open slot</div><div class="sl-dev">Press grab to join, or add a bot helper</div>`);
        if (p) s.appendChild(this._btn('Spectate', () => stepOut(p.device)));
        slots.appendChild(s);
      }
      this._spectatorStrip(watching, app.spectators.map((p) => ({ label: devLabel(p.device), cook: () => stepIn(p.device) })));
      const nb = app.players.filter(isBot).length, np = app.players.length - nb;
      info.innerHTML = !np
        ? `Spectating: ${nb > 1 ? nb + ' bot helpers cook' : 'a bot helper cooks'} while you watch. Hold delegate in a shift to choose their tasks.`
        : !nb
          ? np === 1 ? 'Solo: one chef, all yours. Add a bot helper to hand it chores with Delegate.' : `${np} players ready. Each player controls one chef.`
          : `${np} player${np > 1 ? 's' : ''} and ${nb} bot helper${nb > 1 ? 's' : ''}. Bots count as crew, so the star goals grow too. Hold delegate in a shift to choose their tasks.`;
      botBtn.style.display = app.players.length < 4 ? '' : 'none';
    };
    // someone steps out of the crew to watch (the last chef stays), or a spectator (or a new player) takes a chef
    const stepOut = (d) => {
      if (!app.spectate(d)) return this.toast(lastChefNote({ device: d }, true));
      audio.sfx('uiBack');
      render();
    };
    const stepIn = (d) => {
      if (!app.cook(d)) return this.toast(CREW_FULL);
      audio.sfx('guestArrive');
      render();
    };
    const info = h('div', 'crew-info');
    el.appendChild(info);
    const row = h('div', 'row');
    row.appendChild(this._btn('← Back', () => this.back(), 'back'));
    const botBtn = this._btn('+ Bot helper', () => {
      if (app.players.length >= 4) return;
      let n = 1;
      while (app.players.some((p) => p.device === 'bot' + n)) n++;
      app.players.push({ device: 'bot' + n });
      app.saveBots(app.players.filter(isBot).length);
      audio.sfx('guestArrive');
      render();
    });
    row.appendChild(botBtn);
    row.appendChild(this._btn('Reset to Solo', () => {
      app.players = [{ device: 'kbA' }];
      app.spectators = [];
      app.saveBots(0);
      app.saveSpectating(false);
      render();
    }));
    row.appendChild(this._btn('Ready! →', () => this.show('map'), 'primary'));
    const shownAt = performance.now();
    el.appendChild(row);
    render();
    // raw input polling for joins (called from the app loop)
    let padSig = '';
    this._crewPoll = () => {
      const sig = input.pads().map((p) => p.index + p.id).join('|') + input.padStyleSetting;
      if (sig !== padSig) {
        padSig = sig;
        render();
      }
      if (performance.now() - shownAt < 350) return; // ignore the key press that opened this screen
      // start: a controller's Start button
      for (const p of input.pads()) {
        if (input.padHit(p.index, 9)) {
          audio.sfx('uiClick');
          this.show('map');
          return;
        }
      }
      for (const d of devs()) {
        const s = input.read(d);
        const cooking = app.players.some((p) => p.device === d);
        const spectating = app.spectators.some((p) => p.device === d);
        if (s.grab && cooking) {
          // pressing grab again on a joined device confirms the crew
          audio.sfx('uiClick');
          this.show('map');
          return;
        }
        if (s.grab) {
          // a new player joins, or a spectator steps back in (a full crew gives up its last bot for them)
          if (app.cook(d)) {
            audio.sfx('guestArrive');
            render();
          } else this.toast(CREW_FULL);
        } else if (s.dash && cooking) stepOut(d);
        else if (s.dash && spectating) {
          // a spectator pressing dash again leaves altogether (the last person here stays)
          if (!app.leave(d)) this.toast(LAST_PERSON);
          else {
            audio.sfx('uiBack');
            render();
          }
        }
      }
      if (input.hit(['Escape'])) this.back();
    };
    return el;
  }

  // ------------------------------------------------------------ online
  /** Before a room: host one, or join one by its code. In a room: the code, the invite link and the crew. */
  _online() {
    const app = this.app;
    const net = app.online;
    const el = h('div');
    if (!net) {
      el.appendChild(h('div', 'screen-head', `<h2>Play Online</h2><div class="sh-sub">Cook with friends on their own screens. No accounts, nothing to install: just a room code.</div>`));
      const box = h('div', 'online-start');
      const hostCard = h('div', 'oc-card', `<h3>Host a room</h3><p>You get a four-letter code and an invite link. Friends who open the link land straight in your crew.</p>`);
      hostCard.appendChild(this._btn('Host a room →', () => app.goOnline(), 'primary'));
      const joinCard = h('div', 'oc-card', `<h3>Join a room</h3><p>Got a code from a friend? Type it here.</p><input class="oc-code" maxlength="4" placeholder="CODE" autocomplete="off" spellcheck="false">`);
      const field = joinCard.querySelector('input');
      const go = () => {
        const code = field.value.toUpperCase().replace(/[^A-Z]/g, '');
        if (code.length === 4) app.goOnline(code);
        else field.classList.add('bad');
      };
      field.addEventListener('input', () => {
        field.value = field.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 4);
        field.classList.remove('bad');
      });
      field.addEventListener('keydown', (e) => {
        e.stopPropagation();
        if (e.key === 'Enter') go();
      });
      joinCard.appendChild(this._btn('Join →', go));
      box.appendChild(hostCard);
      box.appendChild(joinCard);
      el.appendChild(box);
      el.appendChild(this._btn('← Back', () => this.back(), 'back'));
      return el;
    }
    const head = h('div', 'screen-head', `<h2>Online Crew</h2><div class="sh-sub online-status"></div>`);
    el.appendChild(head);
    const status = head.querySelector('.online-status');
    const room = h('div', 'online-room');
    el.appendChild(room);
    const slots = h('div', 'crew-slots');
    el.appendChild(slots);
    const watching = h('div', 'crew-watch');
    el.appendChild(watching);
    const info = h('div', 'crew-info');
    el.appendChild(info);
    const row = h('div', 'row');
    row.appendChild(this._btn('← Leave the Room', () => app.leaveOnline(), 'back'));
    const addBtn = this._btn('+ Couch player', () => {
      this._adding = !this._adding;
      render();
    });
    row.appendChild(addBtn);
    const botBtn = this._btn('+ Bot helper', () => {
      net.addBot();
      audio.sfx('guestArrive');
    });
    if (net.isHost) row.appendChild(botBtn);
    const shiftBtn = this._btn('Choose a shift →', () => this.show('map'), 'primary');
    if (net.isHost) row.appendChild(shiftBtn);
    el.appendChild(row);
    // lobby crew changes: the host decides, and a guest's own ask is checked here first so a refusal can say why
    const lastChefOr = (chef, fn) => () => (net.crew.length > 1 ? fn() : this.toast(lastChefNote(chef, net.isHost)));
    const devLabel = (d) => (d === 'any' ? 'Any keyboard or controller' : d.startsWith('kb') ? DEVICES[d].label : 'Gamepad ' + (+d.slice(3) + 1));
    const render = () => {
      if (!this.current || this.current.el !== el) return;
      const open = net.status === 'open';
      status.innerHTML = net.status === 'connecting' ? 'Opening the room…' : net.status === 'reconnecting' ? 'Reconnecting…' : net.isHost ? 'You are hosting. Share the link, then choose a shift when everyone is in.' : net.hostGone ? 'The host lost their connection. Waiting for them to come back…' : 'You are in! The host picks the shift.';
      room.innerHTML = open
        ? `<div class="or-code-label">Room code</div><div class="or-code">${net.code}</div>
           <div class="or-link"><span class="or-url">${net.inviteLink}</span><button class="btn or-copy">Copy invite link</button></div>`
        : '<div class="or-code-label">Connecting…</div>';
      const copy = room.querySelector('.or-copy');
      if (copy) copy.addEventListener('click', () => {
        const done = () => (copy.textContent = 'Copied! ✓');
        if (navigator.clipboard) navigator.clipboard.writeText(net.inviteLink).then(done, () => (copy.textContent = 'Select the link and copy it'));
        else copy.textContent = 'Select the link and copy it';
      });
      slots.innerHTML = '';
      for (let i = 0; i < 4; i++) {
        const p = net.crew[i];
        if (!p) {
          slots.appendChild(h('div', 'slot', `<div class="avatar empty"></div><div class="sl-name">Open slot</div><div class="sl-dev">${net.isHost ? 'Friends join from your link' : 'Waiting for friends'}</div>`));
          continue;
        }
        if (isBot(p)) {
          const s = h('div', 'slot filled bot', botSlot(i));
          if (net.isHost && net.stage === 'lobby') s.appendChild(this._btn('Remove', lastChefOr(p, () => net.dropLocal(p.dev))));
          slots.appendChild(s);
          continue;
        }
        const mine = p.peer === net.peer;
        const who = mine ? 'You' + (net.localDevs.length > 1 ? ` · ${devLabel(p.dev)}` : '') : whoIs(p);
        const away = net.away.has(p.peer);
        const s = h('div', 'slot filled' + (mine ? ' mine' : ''), `<div class="avatar" style="--c:${CHEF_COLORS[i]}"><div class="helm"></div><div class="face"><i></i><i></i></div></div>
          <div class="sl-name" style="color:${CHEF_COLORS[i]}">${CHEF_NAMES[i]}</div>
          <div class="sl-dev">${who}${away ? ' <b class="warn">reconnecting…</b>' : ''}</div>
          ${mine ? `<div class="sl-keys">${keyList(input.hintsFor(p.dev), net.crew.some(isBot))}</div>` : ''}`);
        const lobby = net.stage === 'lobby';
        if (mine && p.dev !== 'any' && lobby) s.appendChild(this._btn('Remove', lastChefOr(p, () => net.dropLocal(p.dev))));
        // your own chef can step out: you spectate, and still steer your share of the bots
        if (mine && p.dev === 'any' && lobby) s.appendChild(this._btn('Spectate', lastChefOr(p, () => net.spectate(p.dev))));
        slots.appendChild(s);
      }
      this._spectatorStrip(watching, net.spectators.map((p) => {
        const mine = p.peer === net.peer;
        const label = (mine ? 'You' : whoIs(p)) + (net.away.has(p.peer) ? ' <b class="warn">reconnecting…</b>' : '');
        const cook = mine && net.stage === 'lobby' ? () => (hasRoomForPerson(net.crew) ? net.cook(p.dev) : this.toast(CREW_FULL)) : null;
        return { label, cook };
      }));
      const n = net.crew.length, nb = net.crew.filter(isBot).length, ns = net.spectators.length;
      const cooks = [n - nb ? `${n - nb} player${n - nb > 1 ? 's' : ''}` : '', nb ? `${nb} bot helper${nb > 1 ? 's' : ''} (bots count as crew for the star goals)` : ''].filter(Boolean).join(' and ');
      const watchers = ns ? ` ${ns > 1 ? ns + ' people spectate' : 'One person spectates'}${nb ? ', still steering a share of the bots with Delegate' : ''}.` : '';
      info.innerHTML = this._adding
        ? 'Press grab on another keyboard or controller to add a couch player on this screen.'
        : !n ? emptyCrewNote(net.isHost)
        : n <= 1 && !ns ? 'Alone for now: one chef. Friends who open your link get chefs of their own, even mid-shift.'
        : `${cooks}.${n - nb ? ' Each player controls one chef on their own screen.' : ''}${watchers}${nb ? ' A friend who joins a full kitchen takes over a bot.' : ''}`;
      addBtn.style.display = n < 4 && net.stage === 'lobby' ? '' : 'none';
      botBtn.style.display = n < 4 && net.stage === 'lobby' ? '' : 'none';
      addBtn.innerHTML = this._adding ? 'Done adding' : '+ Couch player';
      shiftBtn.disabled = !n;
    };
    // who someone else in the room is, as this screen sees them
    const whoIs = (p) => (p.peer === net.hostPeer ? 'Host' : 'Friend');
    net.onChange = render;
    // while adding a couch player, grab on any unclaimed device on this browser claims it
    this._adding = false;
    this._onlinePoll = () => {
      if (!this._adding) return;
      const claimed = net.localDevs;
      for (const d of input.devices()) {
        if (claimed.includes(d) || !input.read(d).grab) continue;
        net.addLocal(d);
        audio.sfx('guestArrive');
        this._adding = false;
        render();
        return;
      }
    };
    setTimeout(render, 0);
    return el;
  }

  // ------------------------------------------------------------ codex
  _codex(opts = {}) {
    const app = this.app;
    const el = h('div');
    const known = new Set();
    LEVELS.forEach((L, i) => app.isUnlocked(i) && L.recipes.forEach((r) => known.add(r)));
    const order = [];
    LEVELS.forEach((L) => L.recipes.forEach((r) => !order.includes(r) && order.push(r)));
    el.appendChild(h('div', 'screen-head', `<h2>Recipe Codex</h2><div class="sh-sub">${known.size} / ${order.length} drinks discovered · unlock shifts to reveal more</div>`));
    const grid = h('div', 'codex');
    for (const id of order) {
      const r = RECIPES[id];
      const k = known.has(id);
      const c = h('div', 'rcard nav' + (k ? '' : ' unknown'), k
        ? `<div class="rc-top">${this.img('drink:' + id, 'ic big')}<div><div class="rc-name">${r.name}</div><div class="rc-app">${this.img(r.app === 'kettle' ? 'kettle' : 'blender', 'ic sm')} ${r.app === 'kettle' ? 'Kettle · brewed hot' : 'Blender'}${r.fizz ? ` · ${this.img('fizz', 'ic sm')} Carbonate` : ''}${r.boba ? ` · ${this.img('bobacup', 'ic sm')} Boba: pearls, seal, straw` : ''}</div></div></div>
           <div class="rc-ing">${r.ing.map((i) => `<span>${this.ingIcon(i)}<em>${INGREDIENTS[i].name}${INGREDIENTS[i].prep ? ` <small>(${INGREDIENTS[i].prep === 'chop' ? 'chopped' : 'ground'})</small>` : ''}</em></span>`).join('')}</div>
           ${r.perk ? `<div class="rc-perk" style="--c:${PERKS[r.perk].color}">★ ${PERKS[r.perk].name}: ${PERKS[r.perk].desc}</div>` : ''}`
        : `<div class="rc-top"><div class="ic big q">?</div><div><div class="rc-name">???</div><div class="rc-app">Undiscovered</div></div></div>`);
      c.tabIndex = 0;
      grid.appendChild(c);
    }
    el.appendChild(grid);
    el.appendChild(this._btn('← Back', () => (opts.onBack ? opts.onBack() : this.back()), 'back'));
    return el;
  }

  // ------------------------------------------------------------ how to play
  _howto(opts = {}) {
    const el = h('div');
    el.appendChild(h('div', 'screen-head', `<h2>How to Play</h2><div class="sh-sub">Keep the galaxy's guests happy before their patience runs out</div>`));
    const K = kbHints('kbA'), K2 = kbHints('kbB');
    const pst = input.anyPadStyle, P = padHints(pst);
    const gl = (g) => (g.startsWith('img:') ? `<img class="ic" src="${this.app.icons[g.slice(4)] || ''}" alt="">` : g);
    const li = (t) => `<li><span class="gl">${gl(t.glyph)}</span><span><b>${t.name}</b>: ${t.desc}</span></li>`;
    el.appendChild(h('div', 'howto', `
      <div class="hcard"><h3>1 · Take an order</h3><p>Guests beam into the lounge and sit at the bar. Their order ticket floats above them, with a patience bar along the bottom. Serve before it runs out!</p></div>
      <div class="hcard"><h3>2 · Prep ingredients</h3><p>Grab ingredients from crates. Fruit, mint, aloe and roots must be <b>chopped</b> on a board; cinnamon, matcha and cacao must be <b>ground</b> in the grinder. Put an ingredient on the board or grinder and your chef starts working right away; they keep going as long as they stay put. Walk away to pause (progress is kept) and tap <b>Use</b> to resume.</p></div>
      <div class="hcard"><h3>3 · Brew or blend</h3><p>Drop prepared ingredients into a <b>kettle</b> on a stove (teas) or a <b>blender jug</b> (smoothies). Wrong combos turn into Murky Sludge. Kettles boil over and catch fire if left too long!</p></div>
      <div class="hcard"><h3>4 · Pour, fizz, serve</h3><p>Take a clean cup from the rack and pour. Fizzy drinks need a trip to the <b>Carbonator</b>. Hand the cup across the bar to the right guest.</p></div>
      <div class="hcard"><h3>5 · Wash up</h3><p>Guests leave dirty cups on the bar. Put them in the sink and your chef starts washing the whole stack right away while standing there (walk away to pause, tap <b>Use</b> to resume). No cups, no service!</p></div>
      <div class="hcard"><h3>Boba tea</h3><p>Boba drinks go in a tall <b>boba cup</b> from the cup stack. Scoop in <b>Boba Pearls</b> and pour in the tea (either order), set the cup in the <b>Sealer</b> to seal the top, then stab in a <b>straw</b> from the straw holder. Boba cups are disposable: guests take them along, so there's nothing to wash.</p></div>
      <div class="hcard"><h3>Murky Sludge</h3><p>Wrong ingredients (or a mix that matches no recipe) turn into <b>Murky Sludge</b>, and a kettle left boiling burns. No guest will drink it. Carry the kettle, jug or cup to the <b>Zapper</b> (the crackling tesla coils) and press <b>Grab</b> to empty it, then start again.</p></div>
      <div class="hcard"><h3>Always know what happens</h3><p>A glowing frame shows exactly which tile you will grab from or place on, with a label for what your button will do. Red means it won't work. The coach bar at the bottom suggests your next step and an arrow points to where to go. Turn it off in Settings. Carrying a finished order, a stream of light in your colour leads you to the guest who wants it, and their ticket lights up with your colour. When several chefs carry the same drink, each gets a different guest, the nearest chef taking the most urgent one; a thin, dim thread means someone nearer has it covered.</p></div>
      <div class="hcard wide"><h3>Bot helpers &amp; Delegate</h3><p>Add a <b>bot helper</b> on the Crew screen (or in an online lobby). A bot counts as a crew member, so the star goals grow with it. A small thought bubble over its head shows what it's working on. Bots split a drink's ingredients between them, throw ingredients straight into kettles and jugs when that's quicker, pass to a teammate who can get an ingredient in sooner (even one on its way to fetch something else), toss things to each other across gaps and walls, and dash on long clear runs. The one on the less urgent job dodges out of the way of the more urgent one, and a bot backs out of a narrow aisle for a person coming the other way. With more than one person playing, the bots are shared out between you (the host first, then by crew slot, then the spectators): your wheel switches only <b>your</b> bots, which light up in your colour while it's open, and everyone's choices are kept on their own device. On shifts with only one drink for a kettle or blender, they start the next one before it's ordered. A bot carrying a drink someone nearer is already taking to the guest leaves it to them and sets its drink by the bar.</p>
        <p>Rather watch? Press <b>Spectate</b> on your crew card (or dash on the Crew screen) to step out of the crew, and <b>Join</b> to cook again. You still get your share of the bots and your Delegate wheel, and a crew can even be all bots, as long as at least one chef cooks.</p>
        <div class="bot-lists">
          <div><h4>Delegate wheel<small>all on to start</small></h4><ul>${DELEGATE_TASKS.map(li).join('')}</ul>
            <p class="dg-how">Hold <b>Delegate</b> (${K.swap} · ${P.swap}) to open the wheel, aim with the mouse or your move keys / stick, and let go to switch that task on or off. Only tasks that fit the shift appear. Your choices are kept between shifts and sessions.</p></div>
          <div><h4>Always<small>whatever is switched on</small></h4><ul>${BOT_WAYS.map(li).join('')}</ul>
            <p class="dg-how">Bots take the most urgent thing first: fires and kettles about to burn, then serving, then cooking. Dishes come before cooking only when the cups are running short.</p></div>
        </div></div>
      <div class="hcard"><h3>Infinite shifts</h3><p>Pick <b>Infinite ∞</b> on a shift's card for a shift with no closing time: guests keep coming, and the clock counts up the time played. It keeps going when the window loses focus, and ends only from the pause menu with <b>End Infinite Shift</b>. Each shift keeps its best infinite score apart from its timed best. With bot helpers cooking and everyone spectating, it plays on by itself: a party screen.</p></div>
      <div class="hcard"><h3>Tips &amp; perks</h3><p>Fast service earns tips; consecutive happy guests multiply them. Some drinks grant crew perks when served (★ on the ticket). Throw ingredients with <b>Use</b> while holding them! Walk into a teammate to shove them out of the way; someone chopping or washing keeps going unless they're pushed away from their station. Each shift keeps its best scores apart for all-human, mixed and all-bot crews, so a kitchen of bots never sets the bar for a crew of people.</p></div>
      <div class="hcard wide"><h3>Controls</h3>
        <table><tr><th></th><th>Keyboard 1</th><th>Keyboard 2</th><th>Gamepad <small>(${PAD_STYLES[pst].label})</small></th></tr>
        <tr><td>Move</td><td>${K.move}</td><td>${K2.move}</td><td>${P.move} / D-pad</td></tr>
        <tr><td>Grab / place / serve</td><td>${K.grab}</td><td>${K2.grab}</td><td>${P.grab}</td></tr>
        <tr><td>Chop / wash / throw / spray</td><td>${K.use}</td><td>${K2.use}</td><td>${P.use} <small>hold to spray</small></td></tr>
        <tr><td>Dash</td><td>${K.dash}</td><td>${K2.dash}</td><td>${P.dash} / triggers</td></tr>
        <tr><td>Delegate (hold, with a bot helper)</td><td>${K.swap}</td><td>${K2.swap}</td><td>${P.swap} / LB / RB</td></tr>
        <tr><td>Pause</td><td>${keyCap('Esc')}<span class="kor">/</span>${keyCap('P')}</td><td>${keyCap('Esc')}<span class="kor">/</span>${keyCap('P')}</td><td>Start / +</td></tr></table><p class="pad-note">Controllers: plug in or pair before (or during) play, then press ${P.grab} on the Crew screen to join (solo works with any controller straight away). Buttons are shown for <b>${PAD_STYLES[pst].label}</b>; change this in Settings → Controller buttons if the letters don't match your controller. The little diamond in every prompt shows which button position to press.</p></div>`));
    el.appendChild(this._btn('← Back', () => (opts.onBack ? opts.onBack() : this.back()), 'back'));
    return el;
  }

  // ------------------------------------------------------------ settings
  _settings(opts = {}) {
    const app = this.app, S = app.save.settings;
    const el = h('div');
    el.appendChild(h('div', 'screen-head', `<h2>Settings</h2><div class="sh-sub">Tune the teahouse to your hardware</div>`));
    const box = h('div', 'settings');
    const slider = (label, key) => {
      const row = h('label', 'set-row', `<span>${label}</span><input class="nav" type="range" min="0" max="100" value="${Math.round(S[key] * 100)}"><b>${Math.round(S[key] * 100)}</b>`);
      const inp = row.querySelector('input');
      inp.addEventListener('input', () => {
        S[key] = inp.value / 100;
        row.querySelector('b').textContent = inp.value;
        app.applySettings();
      });
      inp.addEventListener('keydown', (e) => e.stopPropagation());
      return row;
    };
    box.appendChild(slider('Master volume', 'master'));
    box.appendChild(slider('Music', 'music'));
    box.appendChild(slider('Sound effects', 'sfx'));
    const q = h('div', 'set-row', `<span>Graphics quality</span><div class="seg"></div>`);
    const seg = q.querySelector('.seg');
    for (const [k, lbl] of [['low', 'Low'], ['medium', 'Medium'], ['high', 'High'], ['ultra', 'Ultra']]) {
      const b = this._btn(lbl, () => {
        S.quality = k;
        app.applySettings();
        seg.querySelectorAll('.btn').forEach((x) => x.classList.toggle('sel', x === b));
      }, S.quality === k ? 'sel' : '');
      seg.appendChild(b);
    }
    box.appendChild(q);
    box.appendChild(h('div', 'set-note', 'High and Ultra add planar floor reflections, 4× MSAA and sharper shadows.'));
    const tog = (label, key) => {
      const row = h('div', 'set-row', `<span>${label}</span>`);
      const b = this._btn(S[key] ? 'On' : 'Off', () => {
        S[key] = !S[key];
        b.innerHTML = S[key] ? 'On' : 'Off';
        b.classList.toggle('sel', S[key]);
        app.applySettings();
      }, S[key] ? 'sel' : '');
      row.appendChild(b);
      return row;
    };
    // controller button style
    const pr = h('div', 'set-row', `<span>Controller buttons</span><div class="seg"></div>`);
    const pseg = pr.querySelector('.seg');
    const padOpts = [['auto', 'Auto'], ['xbox', 'Xbox'], ['nintendo', 'Switch'], ['playstation', 'PlayStation']];
    for (const [k, lbl] of padOpts) {
      const b = this._btn(k === 'auto' ? lbl : `${lbl} ${padHints(k).grab}`, () => {
        S.padStyle = k;
        app.applySettings();
        pseg.querySelectorAll('.btn').forEach((x) => x.classList.toggle('sel', x === b));
      }, (S.padStyle || 'auto') === k ? 'sel' : '');
      pseg.appendChild(b);
    }
    box.appendChild(pr);
    box.appendChild(h('div', 'set-note', 'Switch: A (right) grabs, B (bottom) dashes, Y (left) chops, X (top) delegates. Many third-party pads report as Xbox, so choose Switch if yours has B on the bottom.'));
    box.appendChild(tog('Coach hints in shifts', 'coach'));
    box.appendChild(tog('Screen shake', 'shake'));
    box.appendChild(tog('Film grain & lens effects', 'lens'));
    const fs = h('div', 'set-row', `<span>Display</span>`);
    fs.appendChild(this._btn('Toggle fullscreen', () => {
      if (document.fullscreenElement) document.exitFullscreen();
      else document.documentElement.requestFullscreen && document.documentElement.requestFullscreen();
    }));
    box.appendChild(fs);
    const rs = h('div', 'set-row', `<span>Progress</span>`);
    const rb = this._btn('Reset all progress', () => {
      if (rb.dataset.confirm) {
        app.resetProgress();
        rb.innerHTML = 'Progress reset';
        delete rb.dataset.confirm;
      } else {
        rb.dataset.confirm = '1';
        rb.innerHTML = 'Click again to confirm';
      }
    }, 'danger');
    rs.appendChild(rb);
    box.appendChild(rs);
    el.appendChild(box);
    el.appendChild(this._btn('← Back', () => (opts.onBack ? opts.onBack() : this.back()), 'back'));
    return el;
  }

  // ------------------------------------------------------------ credits
  _credits() {
    const el = h('div');
    el.appendChild(h('div', 'screen-head', `<h2>Credits</h2><div class="sh-sub">Made with stardust</div>`));
    el.appendChild(h('div', 'credits', `
      <p><b>Flying Saucer — An Interstellar Teahouse</b></p>
      <p>A cooperative kitchen-chaos game for 1–4 players.</p>
      <p>Recipe names inspired by the Flying Saucer menu notes.</p>
      <p>Custom WebGL2 engine: physically-based shading, soft shadows, planar reflections, HDR bloom and ACES tone mapping.</p>
      <p>All music and sound effects are synthesized live in your browser.</p>
      <p class="dim">Game design &amp; development: Victoria &amp; Claude</p>`));
    el.appendChild(this._btn('← Back', () => this.back(), 'back'));
    return el;
  }

  // ------------------------------------------------------------ pause
  _pause() {
    const app = this.app;
    const el = h('div', 'modal');
    const g = app.game;
    const clock = g.infinite ? `Infinite · ${clockText(g.played)} played` : `${Math.ceil(g.timeLeft)}s left`;
    el.innerHTML = `<div class="panel"><h2>Paused</h2><div class="sh-sub">${g.level.name} · ${clock} · ${g.coins} coins</div></div>`;
    const p = el.querySelector('.panel');
    const guest = app.online && !app.online.isHost;
    p.appendChild(this._btn('Resume', () => app.resume(), 'primary'));
    if (!guest) p.appendChild(this._btn('Restart Shift', () => app.startLevel(g.levelIndex, { infinite: g.infinite })));
    const toPause = () => this.show('pause', { noStack: true, onBack: () => app.resume() });
    p.appendChild(this._btn('How to Play', () => this.show('howto', { noStack: true, onBack: toPause })));
    p.appendChild(this._btn('Recipe Codex', () => this.show('codex', { noStack: true, onBack: toPause })));
    p.appendChild(this._btn('Settings', () => this.show('settings', { noStack: true, onBack: () => this.show('pause', { noStack: true, onBack: () => app.resume() }) })));
    if (guest) p.appendChild(this._btn('Leave the Room', () => app.leaveOnline()));
    // an infinite shift only ends from here, with its score tallied
    else if (g.infinite) p.appendChild(this._btn('End Infinite Shift', () => app.endInfinite()));
    else p.appendChild(this._btn(app.online ? 'Back to the Lobby' : 'Quit to Star Route', () => app.quitToMap()));
    if (app.online) p.appendChild(h('div', 'sh-sub', 'Online: pausing pauses the whole crew.'));
    return el;
  }

  // ------------------------------------------------------------ results
  _results(o) {
    const app = this.app;
    const L = LEVELS[o.levelIndex];
    const el = h('div', 'modal results');
    // an infinite shift earns no stars: its card shows how long it ran instead
    const inf = !!o.infinite;
    const next = !inf && o.levelIndex + 1 < LEVELS.length && app.isUnlocked(o.levelIndex + 1);
    el.innerHTML = `<div class="panel wide">
      <div class="lc-num">${inf ? `Infinite shift ${o.levelIndex + 1} · ${clockText(o.played || 0)} played` : `Shift ${o.levelIndex + 1} complete`}</div>
      <h2>${L.name}</h2>
      ${inf ? '' : `<div class="res-stars">${[0, 1, 2].map((i) => `<div class="rs" data-i="${i}"><i>★</i><span>${o.goals[i]}</span></div>`).join('')}</div>`}
      <div class="res-coins"><span class="coin"></span><b>0</b></div>
      ${o.newBest ? `<div class="newbest">New ${inf ? 'infinite ' : ''}best for ${CREW_KINDS[o.crew] || 'this crew'}!</div>` : ''}
      <div class="res-stats">
        <div><b>${o.served}</b><span>Drinks served</span></div>
        <div><b>${o.tips}</b><span>Tips earned</span></div>
        <div><b>${o.failed}</b><span>Guests lost</span></div>
      </div>
      <div class="row res-btns"></div></div>`;
    const row = el.querySelector('.res-btns');
    if (app.online && !app.online.isHost) {
      row.appendChild(h('div', 'finale', 'Waiting for the host to pick the next shift…'));
      row.appendChild(this._btn('Leave the Room', () => app.leaveOnline()));
    } else row.appendChild(this._btn(app.online ? 'Lobby' : 'Star Route', () => app.quitToMap()));
    if (!(app.online && !app.online.isHost)) {
      row.appendChild(this._btn(inf ? 'Play Again ∞' : 'Retry', () => app.startLevel(o.levelIndex, { infinite: inf })));
      if (next) row.appendChild(this._btn('Next Shift →', () => app.startLevel(o.levelIndex + 1), 'primary'));
    }
    if (!inf && o.levelIndex === LEVELS.length - 1 && !next && o.stars > 0) row.appendChild(h('div', 'finale', 'You ran the Stardust Gala! The galaxy thanks you. ✦'));
    // count-up animation
    const b = el.querySelector('.res-coins b');
    const dur = 1600, t0 = performance.now();
    let lastStar = 0;
    const step = () => {
      const k = Math.min(1, (performance.now() - t0) / dur);
      const v = Math.round(o.coins * (1 - Math.pow(1 - k, 3)));
      b.textContent = v;
      for (let i = lastStar; !inf && i < 3; i++) {
        if (v >= o.goals[i]) {
          el.querySelector(`.rs[data-i="${i}"]`).classList.add('on');
          audio.sfx('star', { i });
          lastStar = i + 1;
        } else break;
      }
      if (k < 1) requestAnimationFrame(step);
      else if (!inf && o.stars === 0) audio.sfx('fail');
    };
    setTimeout(() => requestAnimationFrame(step), 500);
    return el;
  }
}